import { Mesh, MeshBuilder, Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { PlayerCollision } from '../PlayerCollision';
import { ObstacleBehavior } from './ObstacleBehavior';

/**
 * Seeker Beam Behavior – rotates slowly to track the player, fires a visible laser beam.
 * When the sweeping beam crosses the player's position the player takes damage.
 *
 * Config fields used:
 *   speed       – rotation speed (radians/sec) while seeking
 *   beamRange   – max distance at which tracking & damage are active (default 30)
 *   fireInterval – seconds between each shot/pulse (default 2)
 */
export class ObstacleSeekerBeamBehavior extends ObstacleBehavior {
  private time: number = 0;
  private beamMesh: Mesh | null = null;
  private beamActive: boolean = false;
  private beamTimer: number = 0;
  private beamDuration: number = 0.3;
  private collisionMesh: Mesh | null = null;

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleSeekerBeam', startPosition, pathIndex, config);
  }

  initLevel(level: Level): ObstacleSeekerBeamBehavior {
    super.initLevel(level);

    // Register spatial sound
    this.level.game.sound.spatial('fire', this.mesh);

    return this;
  }

  update(deltaTime: number): void {
    if (!this.level?.player) return;

    const dist = this.getPlayerDistance();
    const range = this.config.beamRange ?? 10;

    if (dist > range) {
      this.deactivateBeam();
      return;
    }

    // Slowly rotate toward player
    const playerPos = this.level.player.getPosition();
    const targetAngle = Math.atan2(playerPos.x - this.mesh.position.x, playerPos.z - this.mesh.position.z);
    const currentAngle = this.mesh.rotation.y;
    let delta = targetAngle - currentAngle;
    // Normalize delta to [-PI, PI]
    while (delta > Math.PI) delta -= 2 * Math.PI;
    while (delta < -Math.PI) delta += 2 * Math.PI;
    const rotStep = this.config.speed * deltaTime;
    this.mesh.rotation.y = currentAngle + Math.sign(delta) * Math.min(Math.abs(delta), rotStep);

    // Fire beam periodically
    this.time += deltaTime;
    const fireInterval = this.config.fireInterval ?? 2;
    if (this.time >= fireInterval) {
      this.time = 0;
      this.activateBeam(range);
    }

    // Countdown beam lifetime
    if (this.beamActive) {
      this.beamTimer -= deltaTime;
      if (this.beamTimer <= 0) {
        this.deactivateBeam();
      }
    }
  }

  private activateBeam(range: number): void {
    if (!this.level) return;
    this.beamActive = true;
    this.beamTimer = this.beamDuration;

    // Dispose previous beam
    this.deactivateBeam();

    const origin = this.mesh.position.clone();
    origin.y += 1;

    // Direction: forward of the mesh (world forward rotated by Y)
    const yRot = this.mesh.rotation.y;
    const dir = new Vector3(Math.sin(yRot), 0, Math.cos(yRot));
    const end = origin.add(dir.scale(range));

    // Visible laser cylinder
    this.beamMesh = MeshBuilder.CreateCylinder('seekerBeam', { height: range, diameter: 0.15, tessellation: 6 }, this.level.scene);
    this.beamMesh.position.copyFrom(origin.add(end).scale(0.5));
    this.beamMesh.rotation.x = Math.PI / 2;
    this.beamMesh.rotation.y = yRot;

    // Red glowing material
    const mat =
      this.level.scene.getMaterialByName('seekerBeamMat') ??
      (() => {
        const m = new (require('@babylonjs/core').StandardMaterial)('seekerBeamMat', this.level.scene);
        m.emissiveColor = new (require('@babylonjs/core').Color3)(1, 0.1, 0.1);
        m.disableLighting = true;
        return m;
      })();
    this.beamMesh.material = mat;

    // Invisible collision box along the beam
    this.collisionMesh = MeshBuilder.CreateBox('seekerBeamCollision', { width: 0.4, height: range, depth: 0.4 }, this.level.scene);
    this.collisionMesh.position.copyFrom(this.beamMesh.position);
    this.collisionMesh.rotation.copyFrom(this.beamMesh.rotation);
    this.collisionMesh.isVisible = false;
    this.collisionMesh.isPickable = false;

    this.collisionMesh.metadata = {
      playerCollision: new PlayerCollision(false, (level, player, event) => {
        if (player.vulnerable && !level.game.config.godMode) {
          player.collide(event, 'Hit by seeker beam!');
        }
      }),
    };
    this.level.player?.registerCollisionMesh(this.collisionMesh);
    this.beamActive = true;

    this.level.game.sound.play('fire', this.mesh);
  }

  private deactivateBeam(): void {
    if (this.collisionMesh) {
      this.level?.player?.unregisterCollisionMesh(this.collisionMesh);
      this.collisionMesh.dispose();
      this.collisionMesh = null;
    }
    if (this.beamMesh) {
      this.beamMesh.dispose();
      this.beamMesh = null;
    }
    this.beamActive = false;
  }

  detach(): void {
    this.deactivateBeam();
    super.detach();
  }

  clone(): ObstacleSeekerBeamBehavior {
    return new ObstacleSeekerBeamBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
